(function (root, factory) {
  const api = factory();

  if (typeof module === "object" && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.VendeyVoiceUtils = api;
  }
})(typeof globalThis !== "undefined" ? globalThis : this, function () {
  function normalizarTexto(valor) {
    return String(valor == null ? "" : valor)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }

  function distanciaEdicion(a, b) {
    const izquierda = String(a || "");
    const derecha = String(b || "");
    if (izquierda === derecha) return 0;
    if (!izquierda.length) return derecha.length;
    if (!derecha.length) return izquierda.length;
    const anterior = Array.from({ length: derecha.length + 1 }, (_, indice) => indice);
    const actual = new Array(derecha.length + 1);
    for (let i = 1; i <= izquierda.length; i += 1) {
      actual[0] = i;
      for (let j = 1; j <= derecha.length; j += 1) {
        const costo = izquierda[i - 1] === derecha[j - 1] ? 0 : 1;
        actual[j] = Math.min(
          actual[j - 1] + 1,
          anterior[j] + 1,
          anterior[j - 1] + costo
        );
      }
      for (let j = 0; j <= derecha.length; j += 1) anterior[j] = actual[j];
    }
    return anterior[derecha.length];
  }

  function similitudPalabras(a, b) {
    const izquierda = normalizarTexto(a);
    const derecha = normalizarTexto(b);
    const maximo = Math.max(izquierda.length, derecha.length);
    if (!maximo) return 1;
    return 1 - (distanciaEdicion(izquierda, derecha) / maximo);
  }

  function variantesPalabra(palabra) {
    const variantes = [palabra];
    if (palabra.length > 4 && palabra.endsWith("s")) {
      variantes.push(palabra.slice(0, -1));
    }
    if (palabra.length > 5 && palabra.endsWith("es")) {
      variantes.push(palabra.slice(0, -2));
    }
    if (palabra.length > 5 && /[ao]$/.test(palabra)) {
      variantes.push(palabra.slice(0, -1));
    }
    return Array.from(new Set(variantes));
  }

  function palabraCoincide(nombre, palabra) {
    const palabrasNombre = normalizarTexto(nombre).split(" ").filter(Boolean);
    return variantesPalabra(palabra).some(variante =>
      variante.length >= 3 &&
      (
        palabrasNombre.some(token => token.includes(variante) || variante.includes(token)) ||
        (variante.length >= 4 &&
          palabrasNombre.some(token =>
            token.length >= 4 && similitudPalabras(token, variante) >= 0.74
          ))
      )
    );
  }

  function puntuarProducto(producto, consulta) {
    const nombre = normalizarTexto(producto && producto.CPRODUCTODESC);
    const buscado = normalizarTexto(consulta);
    if (!nombre || !buscado) return -1;
    if (nombre === buscado) return 1000;
    if (nombre.startsWith(buscado)) return 800 - nombre.length;
    if (nombre.includes(buscado)) return 650 - nombre.length;

    const palabras = buscado
      .split(" ")
      .filter(palabra => palabra && !["de", "del", "la", "el", "los", "las", "para", "con"].includes(palabra));
    const coincidencias = palabras.filter(palabra =>
      palabraCoincide(nombre, palabra)
    ).length;
    if (!coincidencias) return -1;
    return (coincidencias / palabras.length) * 500 - nombre.length;
  }

  function buscarProductos(productos, consulta, limite) {
    const maximo = Math.max(1, Number(limite) || 8);
    const palabrasConsulta = normalizarTexto(consulta)
      .split(" ")
      .filter(palabra => palabra && !["de", "del", "la", "el", "los", "las", "para", "con"].includes(palabra));
    return (Array.isArray(productos) ? productos : [])
      .map(producto => ({ producto, puntaje: puntuarProducto(producto, consulta) }))
      .filter(resultado => resultado.puntaje >= 0)
      .sort((a, b) => b.puntaje - a.puntaje)
      .slice(0, maximo)
      .map(resultado => ({
        id: String(resultado.producto.NCODPRODUCTO),
        nombre: String(resultado.producto.CPRODUCTODESC || ""),
        precio: Number(resultado.producto.NPRECIO || 0),
        observaciones: String(resultado.producto.COBSERVACIONES || ""),
        puntaje: Number(resultado.puntaje.toFixed(2)),
        coincidenciaCompleta: palabrasConsulta.every(palabra =>
          palabraCoincide(resultado.producto.CPRODUCTODESC, palabra)
        )
      }));
  }

  function elegirProductoConfiable(resultados, consulta) {
    const opciones = Array.isArray(resultados) ? resultados : [];
    const normalizado = normalizarTexto(consulta);
    const exacto = opciones.find(producto =>
      normalizarTexto(producto.nombre) === normalizado
    );
    if (exacto) return exacto;
    if (opciones.length === 1) return opciones[0];

    const primero = opciones[0];
    const segundo = opciones[1];
    if (!primero || !primero.coincidenciaCompleta) return null;
    if (!segundo?.coincidenciaCompleta) return primero;
    return primero.puntaje - segundo.puntaje >= 25 ? primero : null;
  }

  function mapearTipoDocumento(valor) {
    const tipo = normalizarTexto(valor);
    if (tipo.includes("factura") || tipo === "f") return "01";
    if (tipo.includes("boleta") || tipo === "b") return "03";
    if (tipo.includes("ticket") || tipo.includes("nota") || tipo === "t") return "12";
    return "";
  }

  function crearPropuesta(argumentos, productos) {
    const catalogo = new Map(
      (Array.isArray(productos) ? productos : []).map(producto => [
        String(producto.NCODPRODUCTO),
        producto
      ])
    );
    const itemsSolicitados = Array.isArray(argumentos && argumentos.items)
      ? argumentos.items
      : [];
    const items = [];
    const noEncontrados = [];

    itemsSolicitados.forEach(item => {
      const id = String(item && (item.product_id || item.id) || "");
      const producto = catalogo.get(id);
      const cantidad = Number(item && item.quantity);
      if (!producto || !Number.isFinite(cantidad) || cantidad <= 0) {
        noEncontrados.push(id || "sin código");
        return;
      }
      items.push({
        id,
        nombre: String(producto.CPRODUCTODESC || ""),
        precio: Number(producto.NPRECIO || 0),
        cantidad
      });
    });

    const montoPagado = Number(argumentos && argumentos.paid_amount);
    return {
      id: "voz-" + Date.now(),
      items,
      noEncontrados,
      tipoDocumento: mapearTipoDocumento(argumentos && argumentos.document_type),
      formaPago: String(argumentos && argumentos.payment_method || "").trim(),
      montoPagado: Number.isFinite(montoPagado) && montoPagado > 0 ? montoPagado : null,
      requiereConfirmacion: true
    };
  }

  const NUMEROS_HABLADOS = {
    un: 1,
    uno: 1,
    una: 1,
    dos: 2,
    tres: 3,
    cuatro: 4,
    cinco: 5,
    seis: 6,
    siete: 7,
    ocho: 8,
    nueve: 9,
    diez: 10,
    media: 0.5,
    medio: 0.5
  };

  function extraerCantidadYProducto(texto) {
    let producto = normalizarTexto(texto)
      .replace(/\bpor favor\b/g, " ")
      .replace(/\b(?:al|a el)\s+(?:pedido|carrito|ticket|venta)\b/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    let cantidad = 1;
    const coincidencia = producto.match(
      /^(?:(\d+(?:[.,]\d+)?)|(un|uno|una|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|medio|media))\s+(?:unidad(?:es)?\s+de\s+)?(.+)$/
    );
    if (coincidencia) {
      cantidad = coincidencia[1]
        ? Number(coincidencia[1].replace(",", "."))
        : NUMEROS_HABLADOS[coincidencia[2]];
      producto = coincidencia[3].trim();
    }
    producto = producto
      .replace(/^(?:de|del|el|la|los|las)\s+/, "")
      .replace(/\s+(?:al|a)\s+(?:pedido|carrito|ticket|venta)$/, "")
      .trim();
    return {
      cantidad: Number.isFinite(cantidad) && cantidad > 0 ? cantidad : 1,
      producto
    };
  }

  function interpretarComandoPos(texto) {
    const original = String(texto == null ? "" : texto).trim();
    const normalizado = normalizarTexto(original);
    if (!normalizado) {
      return { scope: "pos", intent: "no_entendida", transcript: original };
    }

    const documento = mapearTipoDocumento(normalizado);
    const formaPago = ["efectivo", "tarjeta", "yape", "plin", "transferencia"]
      .find(medio => normalizado.includes(medio)) || "";
    const monto = normalizado.match(
      /\b(?:pago|pagado|recibido|recibe|con)\s+(?:s\/?\s*)?(\d+(?:[.,]\d{1,2})?)\b/
    );
    if (/\b(?:internet|web|google|noticias|clima|tiempo|futbol|politica|receta|wikipedia)\b/.test(normalizado)) {
      return {
        scope: "out_of_scope",
        intent: "fuera_de_alcance",
        transcript: original
      };
    }
    const patronesAgregar = [
      /^(?:agrega|agregar|agregame|anade|anadir|incluye|incluir|pon|poner)\s+(.+)$/,
      /^(?:vende|vender|vendeme)\s+(.+)$/,
      /^(?:quiero|deseo|llevare|llevar)\s+(?:comprar|llevar)?\s*(.+)$/,
      /^(?:realiza|realizar|haz|hacer|crea|crear|prepara|preparar)\s+(?:una\s+)?venta\s+(?:con|de)\s+(.+)$/
    ];

    for (const patron of patronesAgregar) {
      const coincidencia = normalizado.match(patron);
      if (!coincidencia) continue;
      const detalle = coincidencia[1]
        .replace(/\b(?:en|con)\s+(?:ticket|boleta|factura)\b.*$/, "")
        .replace(/\b(?:y\s+)?(?:paga|pago|pagado|recibido|recibe)\s+(?:con\s+)?(?:efectivo|tarjeta|yape|plin|transferencia)?\s*(?:s\/?\s*)?\d*(?:[.,]\d+)?\s*$/, "")
        .trim();
      const producto = extraerCantidadYProducto(detalle);
      return {
        scope: "pos",
        intent: "agregar_producto",
        transcript: original,
        productQuery: producto.producto,
        quantity: producto.cantidad,
        documentType: documento,
        paymentMethod: formaPago,
        paidAmount: monto ? Number(monto[1].replace(",", ".")) : null
      };
    }

    const consulta = normalizado.match(
      /^(?:busca|buscar|encuentra|encontrar|consulta|consultar|hay|tienes|precio de|cuanto cuesta)\s+(.+)$/
    );
    if (consulta) {
      return {
        scope: "pos",
        intent: "buscar_producto",
        transcript: original,
        productQuery: extraerCantidadYProducto(consulta[1]).producto
      };
    }

    if (documento && /\b(?:usar|usa|cambiar|cambia|comprobante|documento|emitir|en)\b/.test(normalizado)) {
      return {
        scope: "pos",
        intent: "seleccionar_comprobante",
        transcript: original,
        documentType: documento
      };
    }

    if (formaPago && /\b(?:pagar|paga|pago|forma|medio|usar|usa)\b/.test(normalizado)) {
      return {
        scope: "pos",
        intent: "seleccionar_pago",
        transcript: original,
        paymentMethod: formaPago,
        paidAmount: monto ? Number(monto[1].replace(",", ".")) : null
      };
    }

    if (/\b(?:venta|producto|precio|stock|catalogo|pedido|carrito|ticket|boleta|factura|pagar|pago|vuelto|cliente|vendedor|caja)\b/.test(normalizado)) {
      return { scope: "pos", intent: "no_entendida", transcript: original };
    }

    return {
      scope: "out_of_scope",
      intent: "fuera_de_alcance",
      transcript: original
    };
  }

  return {
    normalizarTexto,
    similitudPalabras,
    buscarProductos,
    elegirProductoConfiable,
    mapearTipoDocumento,
    crearPropuesta,
    interpretarComandoPos
  };
});
